import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { QueryStatus, authAxios } from "../../utils";
import { RootState } from "../../redux/store";
import {
  CarIconApi,
  GetSivDataCarIconPresignedUrlQuery,
  GetSivDataCarIconPresignedUrlResponse,
  UpdateSivDataCarIconStatusPayload,
} from "./carIconsApi";

export interface CarIconsState {
  carIconsToReviewStatus: QueryStatus;
  carIconsToReview: CarIconApi[];
  carIconPresignedUrlStatus: QueryStatus;
  carIconPresignedUrl: string | null;
  updateCarIconStatusStatus: QueryStatus;
}

const initialState: CarIconsState = {
  carIconsToReviewStatus: "idle",
  carIconsToReview: [],
  carIconPresignedUrlStatus: "idle",
  carIconPresignedUrl: null,
  updateCarIconStatusStatus: "idle",
};

export const carIconsToReviewAsync = createAsyncThunk(
  "carIconsToReview/call",
  async () => {
    const axios = authAxios();
    const response = await axios.get<CarIconApi[]>(
      `/vehicles/car_icons/to_review`,
    );
    return response.data;
  },
);

export const carIconPresignedUrlAsync = createAsyncThunk(
  "carIconPresignedUrl/call",
  async (query: GetSivDataCarIconPresignedUrlQuery) => {
    const axios = authAxios();
    const response = await axios.get<GetSivDataCarIconPresignedUrlResponse>(
      `/vehicles/car_icons/get_presigned_url`,
      { params: query },
    );
    return response.data;
  },
);

export const updateCarIconStatusAsync = createAsyncThunk(
  "updateCarIconStatus/call",
  async (payload: UpdateSivDataCarIconStatusPayload) => {
    const axios = authAxios();
    await axios.post(`/vehicles/car_icons/update_status`, payload);
  },
);

export const carIconsSlice = createSlice({
  name: "carIcons",
  initialState,
  reducers: {
    resetCarIconPresignedUrl(state) {
      state.carIconPresignedUrlStatus = "idle";
      state.carIconPresignedUrl = null;
    },
    resetUpdateCarIconStatusStatus(state) {
      state.updateCarIconStatusStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(carIconsToReviewAsync.pending, (state) => {
        state.carIconsToReviewStatus = "processing";
      })
      .addCase(carIconsToReviewAsync.fulfilled, (state, action) => {
        state.carIconsToReviewStatus = "success";
        state.carIconsToReview = action.payload;
      })
      .addCase(carIconsToReviewAsync.rejected, (state) => {
        state.carIconsToReviewStatus = "failed";
      })
      .addCase(carIconPresignedUrlAsync.pending, (state) => {
        state.carIconPresignedUrlStatus = "processing";
      })
      .addCase(carIconPresignedUrlAsync.fulfilled, (state, action) => {
        state.carIconPresignedUrlStatus = "success";
        state.carIconPresignedUrl = action.payload.url;
      })
      .addCase(carIconPresignedUrlAsync.rejected, (state) => {
        state.carIconPresignedUrlStatus = "failed";
      })
      .addCase(updateCarIconStatusAsync.pending, (state) => {
        state.updateCarIconStatusStatus = "processing";
      })
      .addCase(updateCarIconStatusAsync.fulfilled, (state) => {
        state.updateCarIconStatusStatus = "success";
        state.carIconsToReviewStatus = "idle";
      })
      .addCase(updateCarIconStatusAsync.rejected, (state) => {
        state.updateCarIconStatusStatus = "failed";
      });
  },
});

export const selectCarIconsToReview = (state: RootState) =>
  state.carIcons.carIconsToReview;
export const selectCarIconsToReviewStatus = (state: RootState) =>
  state.carIcons.carIconsToReviewStatus;
export const selectCarIconPresignedUrl = (state: RootState) =>
  state.carIcons.carIconPresignedUrl;
export const selectCarIconPresignedUrlStatus = (state: RootState) =>
  state.carIcons.carIconPresignedUrlStatus;
export const selectUpdateCarIconStatusStatus = (state: RootState) =>
  state.carIcons.updateCarIconStatusStatus;

export default carIconsSlice.reducer;
